import { ThemeProvider } from "@/components/ThemeProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/providers/AuthProvider";
import { LabelProvider } from "@/providers/LabelProvider";
import { PlayerProvider } from "@/components/player/PlayerProvider";
import { Toaster } from "@/components/ui/sonner";
import { MotionConfig } from "framer-motion";
import { AppRoutes } from "@/routes";
import { DesktopOnlyScreen } from "@/components/DesktopOnlyScreen";
import { opensOnAnyDevice, useDeviceDetection } from "@/hooks/useDeviceDetection";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export function AppContent() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isDesktop } = useDeviceDetection();

  // Handle password reset links immediately on component mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("type=recovery") || hash.includes("error=")) {
      // Only navigate if not already on the update-password page
      // to avoid stripping the hash params that update-password.tsx needs
      if (window.location.pathname !== "/auth/update-password") {
        navigate("/auth/update-password" + hash, { replace: true });
      }
    }
  }, [navigate]);

  // There is deliberately NO loading state here. The device check is synchronous
  // (see useDeviceDetection), so `isDesktop` is right on the first render. The
  // full-screen loader that used to sit here was a fifth hand-rolled copy of the
  // Zen mark — static, glow-less, driven by an `animation: "morphingGlow"` naming a
  // keyframe that only exists inside SearchPage's inline <style> — and it was the
  // first of three marks the user saw at three different moments on every boot.
  if (!isDesktop && !opensOnAnyDevice(pathname)) {
    return <DesktopOnlyScreen />;
  }

  return <AppRoutes />;
}

export default function App() {
  return (
    <ThemeProvider>
      <MotionConfig reducedMotion="user">
        <TooltipProvider>
          <AuthProvider>
            <LabelProvider>
              <PlayerProvider>
                <AppContent />
                {/* The one toast surface: DESIGN tokens (components/ui/sonner.tsx). Clickable over an
                    open dialog: a modal dialog turns pointer events off on the page behind it. */}
                <Toaster />
              </PlayerProvider>
            </LabelProvider>
          </AuthProvider>
        </TooltipProvider>
      </MotionConfig>
    </ThemeProvider>
  );
}
